import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { UpdatePaymentMethodModal } from './UpdatePaymentMethodModal';
import FigoLottie from '../../common/FigoLottie';
import { GenericModal } from '../../common/petCloud/GenericModal';

import { useWebviewMultibrand } from '../../../hooks';
import {
  MY_ACCOUNT, WEB_VIEW_TYPES,
  getBillingCycleType,
} from '../../../constants';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import { getThemeLogoById } from '../../../theme/theme.utils';
import { isGPIPolicy } from '../myAccount.utils';
import {
  globalContactUsModalToggled,
  webViewMultibrandReset,
} from '../../../actions';
import { currencyFormat } from '../../../services/utils';
import { EDITABLE_MARKET_CHANNELS } from './billingAndPayments.constants';

import './css/PolicyCard.css';

const PolicyCard = ({
  billingPayments = {},
  breadCrumbs = null,
  marketingChannelId = 0,
  pets = [],
  policyId = '',
  policyNumber = '',
  policy,
}) => {
  const { t } = useTranslation('myAccount');
  const history = useHistory();
  const location = useLocation();
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const BILLING_CYCLE = getBillingCycleType();
  const dispatch = useDispatch();

  const [
    updatePaymentModalVisible,
    setUpdatePaymentModalVisible,
  ] = useState(false);
  const [
    webviewErrorModalVisible,
    setWebviewErrorModalVisible,
  ] = useState(false);
  const logo = useMemo(() => {
    const theme = getThemeLogoById(marketingChannelId);
    return theme.dark;
  }, [marketingChannelId]);
  const isNotPPIOnePack = marketingChannelId
    !== MARKETING_CHANNEL_ID.ppiG;
  const {
    error,
    getWebView,
    isLoading,
    title,
    url,
  } = useWebviewMultibrand();

  useEffect(() => {
    if (url && !isLoading && breadCrumbs && selectedPolicy) {
      breadCrumbs.onAddBreadcrumbItem({
        extraData: {
          marketingChannelId: selectedPolicy?.MarketingChannelId,
          policyNumber,
          title,
          url,
        },
        id: MY_ACCOUNT.billingAndPaymentDetails,
        name: t('breadCrumbs.breadCrumbPolicy', {
          policyNumber,
        }),
      });
    }
  }, [url, isLoading, breadCrumbs, selectedPolicy]);

  const onAddFigoBreadcrumbVariant = () => {
    breadCrumbs.onAddBreadcrumbItem({
      extraData: {
        marketingChannelId,
        policyId: policy.PolicyId,
        policyNumber,
      },
      id: MY_ACCOUNT.billingAndPaymentDetails,
      name: t('breadCrumbs.breadCrumbPolicy', {
        policyNumber,
      }),
    });
  };

  const onViewDetails = () => {
    history.push(`${location.pathname}/${policyNumber}${location.search}`);
  };

  function onClickViewDetails() {
    setSelectedPolicy(policy);

    if (isGPIPolicy(policy?.PolicyNumber)) {
      setUpdatePaymentModalVisible(true);
      return;
    }

    if (EDITABLE_MARKET_CHANNELS.includes(marketingChannelId)) {
      onViewDetails();
      onAddFigoBreadcrumbVariant();
    } else {
      getWebView({
        companyId: marketingChannelId,
        coveragePeriodId: policy.CoveragePeriodId,
        policyId,
        policyNumber,
        webViewType: WEB_VIEW_TYPES.billingAndPayment,
      });
    }
  }

  useEffect(() => {
    if (!isLoading && error && selectedPolicy) {
      setSelectedPolicy(null);
      setWebviewErrorModalVisible(true);
      dispatch(webViewMultibrandReset());
    }
  }, [error, isLoading, selectedPolicy]);

  const onCloseWebviewErrorModal = () => setWebviewErrorModalVisible(false);

  const onContactUsClick = () => {
    setWebviewErrorModalVisible(false);
    dispatch(globalContactUsModalToggled());
  };

  const hideUpdatePaymentMethodModal = () => {
    setUpdatePaymentModalVisible(false);
  };

  return (
    <div className="Policy-card-container">
      <div className="Policy-card-data-container">
        <div className="Policy-card-price-container">
          <img
            alt="Policy Logo"
            className={`Policy-card-price-img 
            Policy-card-price-img-${marketingChannelId}`}
            loading="lazy"
            src={logo}
          />

          {isNotPPIOnePack
            ? (
              <p className="Policy-card-price-text">
                {currencyFormat({ value: billingPayments.Premium || 0 })}

                <span>{billingPayments
                  ?.BillingCycle === BILLING_CYCLE.monthly
                  ? t('billingAndPayments.month') : t('billingAndPayments.yr')}
                </span>
              </p>
            )
            : (
              <p className="Policy-card-price-text">
                <span>
                  {t('billingAndPayments.managedBy')}
                  &nbsp;{billingPayments?.GroupName}
                </span>
              </p>
            )}
        </div>

        <div className="Policy-card-details-container">
          <div className="Policy-card-details-item">
            <h6 className="Policy-card-details-item-title">
              {isNotPPIOnePack ? t('billingAndPayments.policyNumber')
                : t('billingAndPayments.certificateNumber')}
            </h6>

            <p className="Policy-card-details-item-content">
              {policyNumber}
            </p>
          </div>

          <div className="Policy-card-details-item">
            {isNotPPIOnePack ? (
              <>
                <h6 className="Policy-card-details-item-title">
                  {t('billingAndPayments.paymentMethod')}
                </h6>

                <p className="Policy-card-details-item-content">
                  {billingPayments?.PaymentMethod}
                </p>
              </>
            )
              : (
                <>
                  <h6 className="Policy-card-details-item-title">
                    {t('billingAndPayments.petsOnThisPolicy')}
                  </h6>

                  <p className="Policy-card-details-item-content">
                    {pets.map((pet) => pet.PetName).join(', ')}
                  </p>
                </>
              )}
          </div>

          <div className="Policy-card-details-item">
            {isNotPPIOnePack
              ? (
                <>
                  <h6 className="Policy-card-details-item-title">
                    {t('billingAndPayments.petsOnThisPolicy')}
                  </h6>
                  <p className="Policy-card-details-item-content">
                    {pets.map((pet) => pet.PetName).join(', ')}
                  </p>
                </>
              )
              : (
                <>
                  <h6 className="Policy-card-details-item-title-onePack">
                    {t('billingAndPayments.petsOnThisPolicyOnePack')}
                  </h6>
                  <p className="Policy-card-details-item-content-onePack">
                    {t('billingAndPayments.contentOnePack')}
                  </p>
                </>
              )}
          </div>
        </div>
      </div>

      {isNotPPIOnePack && (
        <button
          className="Policy-card-button-details"
          disabled={isLoading}
          onClick={onClickViewDetails}
          type="button"
        >
          {isLoading && selectedPolicy
            ? <FigoLottie />
            : t('paymentHistorySection.viewDetails')}
        </button>
      )}

      <UpdatePaymentMethodModal
        onClose={hideUpdatePaymentMethodModal}
        show={updatePaymentModalVisible}
      />

      <GenericModal
        description={(
          <Trans
            components={{ bold: <b /> }}
            i18nKey="webviewErrorModal.description"
            t={t}
          />
        )}
        handleOutClick={onCloseWebviewErrorModal}
        onPrimaryClick={onContactUsClick}
        primaryButtonText={t('webviewErrorModal.primaryButtonText')}
        show={webviewErrorModalVisible}
        title={t('webviewErrorModal.title')}
      />
    </div>
  );
};

export default PolicyCard;
